import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { useForm, UseFormRegister, FieldErrors, UseFormHandleSubmit, UseFormSetValue } from "react-hook-form";
import SubmissionForm from "@/components/submissions/SubmissionForm";
import { projectApi } from "@/services/projectService";
import { userApi, GetUsersResponse } from "@/services/userService";
import { CreateProjectRequestDto } from "@/dto/create-project-request.dto";
import { ServiceType } from "@/enums/project.enum";
import { Roles } from "@/enums/roles.enum";
import MobileCard from "@/components/mobile/MobileCard";
import { useDeviceType } from "@/hooks/use-mobile";
import { ahjApi } from "@/services/ahjService";
import { IAHJ } from "@/types/ahj";
import { AHJFormDialog } from "@/components/ahj/AHJFormDialog";
import { AHJRequirementsUpload } from "@/components/ahj/AHJRequirementsUpload";

const services = [
  {
    value: ServiceType.PERMITTING_DRAWING_AND_SUBMITTAL,
    label: "Permitting Service: Drawing & Submittal",
  },
  {
    value: ServiceType.PERMITTING_DRAWING_ONLY,
    label: "Permitting Service: Drawing Only",
  },
  {
    value: ServiceType.PERMITTING_SUBMITTAL_ONLY,
    label: "Permitting Service: Submittal Only",
  },
] as const;

type Service = (typeof services)[number]["value"];

interface ProjectFormValues {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  buildingOwner: string;
  buildingOwnerAddress: string;
  service: Service;
  projectManager?: string; // User ID for QWS Project Manager
  ahj?: string; // AHJ ID
}

const NewProject = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();

  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<"project" | "ahj-upload" | "submission">(
    "project"
  );
  const [createdProject, setCreatedProject] = useState<string | null>(null);
  const [ahjDialogOpen, setAhjDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<ProjectFormValues>({
    defaultValues: {
      service: ServiceType.PERMITTING_DRAWING_AND_SUBMITTAL,
    },
  });

  // Fetch all users for project manager selection (Admin only)
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: userApi.getUsers,
    enabled: user?.role === Roles.ADMIN,
  });

  // Filter to only show Admin users as project managers
  const adminUsers = usersData?.users.filter(u => u.role === Roles.ADMIN) || [];

  // Fetch all AHJs
  const { data: ahjsData } = useQuery({
    queryKey: ["ahjs"],
    queryFn: () => ahjApi.getAHJs({}),
  });

  useEffect(() => {
    if (searchParams.get("projectId")) {
      setCreatedProject(searchParams.get("projectId"));
      // If we have a project ID but step is project, move to submission or ahj-upload
      // We can't easily know if AHJ upload is needed without fetching project, 
      // but for now let's default to submission if returning
      if (currentStep === "project") {
         setCurrentStep("submission");
      }
    }
  }, [searchParams, currentStep]);

  const { mutate: createProject } = useMutation({
    mutationFn: projectApi.createProject,
    onSuccess: (newProject) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Project created",
        description: "Your project has been created successfully",
      });
      setCreatedProject(newProject._id);
      setSearchParams(`?${new URLSearchParams({ projectId: newProject._id })}`);
      
      if (newProject.ahj) {
        setCurrentStep("ahj-upload");
      } else {
        setCurrentStep("submission");
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive",
      });
    },
  });

  const handleAHJCreated = (ahj: IAHJ) => {
    setValue("ahj", ahj._id);
    setAhjDialogOpen(false);
  };

  const onSubmit = async (data: ProjectFormValues) => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      const projectData: CreateProjectRequestDto = {
        name: data.name,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        buildingOwner: data.buildingOwner,
        buildingOwnerAddress: data.buildingOwnerAddress,
        service: data.service,
        projectManager: data.projectManager,
        ahj: data.ahj,
      };
      createProject(projectData);
    } catch (error) {
      console.error("Error creating project:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const MobileProjectDetailsForm = ({ 
    register, 
    errors, 
    handleSubmit, 
    onSubmit, 
    isSubmitting,
    setValue,
    usersData,
    ahjsData,
    isAdmin,
    setAhjDialogOpen,
  }: {
    register: UseFormRegister<ProjectFormValues>;
    errors: FieldErrors<ProjectFormValues>;
    handleSubmit: UseFormHandleSubmit<ProjectFormValues>;
    onSubmit: (data: ProjectFormValues) => void;
    isSubmitting: boolean;
    setValue: UseFormSetValue<ProjectFormValues>;
    usersData: GetUsersResponse | undefined;
    ahjsData: { ahjs: IAHJ[] } | undefined;
    isAdmin: boolean;
    setAhjDialogOpen: (open: boolean) => void;
  }) => (
    <div className="space-y-4">
      <MobileCard title="Project Information" className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Project Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Enter project name"
              {...register("name", { required: "Project name is required" })}
            />
            {errors.name && (
              <p className="text-red-500 text-xs">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="service" className="text-sm font-medium">
              Service Type <span className="text-red-500">*</span>
            </Label>
            <Select onValueChange={(value) => register("service").onChange({ target: { value } })}>
              <SelectTrigger>
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.value} value={service.value}>
                    {service.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.service && (
              <p className="text-red-500 text-xs">{errors.service.message}</p>
            )}
          </div>
        </div>
      </MobileCard>

      <MobileCard title="Building Owner" className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="buildingOwner" className="text-sm font-medium">
              Building Owner <span className="text-red-500">*</span>
            </Label>
            <Input
              id="buildingOwner"
              placeholder="Enter building owner name"
              {...register("buildingOwner", { required: "Building owner is required" })}
            />
            {errors.buildingOwner && (
              <p className="text-red-500 text-xs">{errors.buildingOwner.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="buildingOwnerAddress" className="text-sm font-medium">
              Building Owner Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="buildingOwnerAddress"
              placeholder="Enter building owner address"
              {...register("buildingOwnerAddress", { required: "Building owner address is required" })}
            />
            {errors.buildingOwnerAddress && (
              <p className="text-red-500 text-xs">{errors.buildingOwnerAddress.message}</p>
            )}
          </div>
        </div>
      </MobileCard>

      {isAdmin && (
        <MobileCard title="QWS Project Manager" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="projectManager" className="text-sm font-medium">
              Assign QWS Project Manager (Admin only)
            </Label>
            <Select onValueChange={(value) => setValue("projectManager", value === "none" ? undefined : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select Admin user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {usersData?.users.filter(u => u.role === Roles.ADMIN).map(u => (
                  <SelectItem key={u._id} value={u._id}>
                    {`${u.firstName} ${u.lastName}`.trim() || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </MobileCard>
      )}

      <MobileCard title="Authority Having Jurisdiction (AHJ)" className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ahj" className="text-sm font-medium">
            Select AHJ
          </Label>
          <Select onValueChange={(value) => {
            if (value === "add-new") {
              setAhjDialogOpen(true);
            } else {
              setValue("ahj", value === "none" ? undefined : value);
            }
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Select AHJ" />
            </SelectTrigger>
            <SelectContent>
              <div className="sticky top-0 bg-background border-b">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAhjDialogOpen(true);
                  }}
                  className="w-full px-2 py-1.5 text-sm font-medium text-primary hover:bg-accent flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add New AHJ
                </button>
              </div>
              <SelectItem value="none">None</SelectItem>
              {Array.from(
                (ahjsData?.ahjs || [])
                  .sort((a, b) => {
                    if (a.type !== b.type) return a.type.localeCompare(b.type);
                    if (a.state !== b.state) return a.state.localeCompare(b.state);
                    return a.name.localeCompare(b.name);
                  })
                  .reduce((acc, ahj) => {
                    const groupKey = `${ahj.type}-${ahj.state}`;
                    if (!acc.has(groupKey)) {
                      acc.set(groupKey, []);
                    }
                    acc.get(groupKey)?.push(ahj);
                    return acc;
                  }, new Map<string, IAHJ[]>())
                  .entries()
              ).map(([groupKey, ahjs]) => {
                const [type, state] = groupKey.split('-');
                return (
                  <div key={groupKey}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                      {type.charAt(0).toUpperCase() + type.slice(1)} - {state}
                    </div>
                    {ahjs.map((ahj: IAHJ) => (
                      <SelectItem key={ahj._id} value={ahj._id}>
                        {ahj.name} ({ahj.state})
                      </SelectItem>
                    ))}
                  </div>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </MobileCard>

      <MobileCard title="Project Location" className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm font-medium">
              Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="address"
              placeholder="Enter project address"
              {...register("address", { required: "Address is required" })}
            />
            {errors.address && (
              <p className="text-red-500 text-xs">{errors.address.message}</p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium">
                City <span className="text-red-500">*</span>
              </Label>
              <Input
                id="city"
                placeholder="Enter city"
                {...register("city", { required: "City is required" })}
              />
              {errors.city && (
                <p className="text-red-500 text-xs">{errors.city.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="state" className="text-sm font-medium">
                  State <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="state"
                  placeholder="State"
                  {...register("state", { required: "State is required" })}
                />
                {errors.state && (
                  <p className="text-red-500 text-xs">{errors.state.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip" className="text-sm font-medium">
                  ZIP Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="zip"
                  placeholder="ZIP"
                  {...register("zip", { required: "ZIP code is required" })}
                />
                {errors.zip && (
                  <p className="text-red-500 text-xs">{errors.zip.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </MobileCard>

      <div className="space-y-3 pt-4">
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="w-full"
        >
          <ArrowRight className="h-4 w-4 mr-2" />
          {isSubmitting ? "Creating..." : "Begin Submission"}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/projects")}
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  if (currentStep === "submission" && createdProject) {
    return (
      <>
        {isMobile ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep("project")}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-xl font-bold">New Submission</h1>
            </div>
            <MobileCard title="High-Pile Racking Permit Application">
              <p className="text-sm text-muted-foreground mb-4">
                Please complete the form below with details about your high-pile storage system. 
                All fields marked with * are required.
              </p>
              <SubmissionForm projectId={createdProject} />
            </MobileCard>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center">
              <Button
                variant="outline"
                className="mr-4"
                onClick={() => setCurrentStep("project")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Project Details
              </Button>
              <h1 className="text-3xl font-bold">New Permit Submission</h1>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>High-Pile Racking Permit Application</CardTitle>
                <CardDescription>
                  Please complete the form below with details about your high-pile
                  storage system. All fields marked with * are required.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SubmissionForm projectId={createdProject} />
              </CardContent>
            </Card>
          </div>
        )}
      </>
    );
  }

  if (currentStep === "ahj-upload" && createdProject) {
    const ahjId = getValues("ahj");
    if (!ahjId) {
      setCurrentStep("submission");
      return null;
    }

    return (
      <AHJRequirementsUpload
        projectId={createdProject}
        ahjId={ahjId}
        onComplete={() => setCurrentStep("submission")}
        onSkip={() => setCurrentStep("submission")}
      />
    );
  }

  return (
    <>
      {isMobile ? (
        <div className="space-y-4">
          {/* Mobile Header */}
          <div className="flex items-center gap-3 pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/projects")}
              className="flex items-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Projects
            </Button>
          </div>

          {/* Mobile Form */}
          <MobileProjectDetailsForm 
            register={register}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            setValue={setValue}
            usersData={usersData}
            ahjsData={ahjsData}
            isAdmin={user?.role === Roles.ADMIN}
            setAhjDialogOpen={setAhjDialogOpen}
          />
        </div>
      ) : (
        <div className="container max-w-4xl py-8 space-y-8 animate-in fade-in duration-500">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/projects")}
                  className="h-8 w-8 -ml-2 hover:bg-accent hover:text-accent-foreground rounded-full"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">New Project</h1>
              </div>
              <p className="text-muted-foreground pl-8">
                Create a new project to start tracking permits and submissions.
              </p>
            </div>
          </div>

          <Card className="border-border/50 shadow-sophisticated highlight-top overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
              <CardTitle className="text-xl">Project Details</CardTitle>
              <CardDescription>
                Fill out the information below to create a new project. On the next
                step, you will need to fill out all required information in order to
                complete your submittal. If you are not able to, your project will
                be auto-saved in drafts until you are ready to submit.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                <div className="grid gap-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-foreground/80">
                        Project Name <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        placeholder="Enter project name"
                        className="bg-background/50 focus:bg-background transition-colors"
                        {...register("name", {
                          required: "Project name is required",
                        })}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceSelection" className="text-sm font-medium text-foreground/80">
                        Service Selection{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Select
                        value={getValues("service")}
                        onValueChange={(value) => {
                          setValue("service", value as Service, {
                            shouldValidate: true,
                          });
                        }}
                      >
                        <SelectTrigger id="service">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.value} value={service.value}>
                              {service.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2 pt-4">
                    <h3 className="font-medium">Building Information</h3>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="buildingOwner" className="text-sm font-medium text-foreground/80">
                        Building Owner{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="buildingOwner"
                        className="bg-background/50 focus:bg-background transition-colors"
                        {...register("buildingOwner", {
                          required: "Building owner is required",
                        })}
                      />
                      {errors.buildingOwner && (
                        <p className="text-red-500 text-sm">
                          {errors.buildingOwner.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="buildingOwnerAddress" className="text-sm font-medium text-foreground/80">
                        Building Owner Address{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="buildingOwnerAddress"
                        className="bg-background/50 focus:bg-background transition-colors"
                        {...register("buildingOwnerAddress", {
                          required: "Building owner address is required",
                        })}
                      />
                      {errors.buildingOwnerAddress && (
                        <p className="text-red-500 text-sm">
                          {errors.buildingOwnerAddress.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {user?.role === Roles.ADMIN && (
                    <div className="space-y-4 pt-4 border-t border-border/50">
                      <h3 className="font-semibold text-lg">QWS Project Manager</h3>
                      <div className="grid gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="projectManager" className="text-sm font-medium text-foreground/80">
                            Assign QWS Project Manager (Admin only)
                          </Label>
                          <Select onValueChange={(value) => setValue("projectManager", value === "none" ? undefined : value)}>
                            <SelectTrigger id="projectManager" className="bg-background/50 focus:bg-background transition-colors">
                              <SelectValue placeholder="Select Admin user" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {adminUsers.map(u => (
                                <SelectItem key={u._id} value={u._id}>
                                  {`${u.firstName} ${u.lastName}`.trim() || u.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4 pt-4 border-t border-border/50">
                    <h3 className="font-semibold text-lg">Authority Having Jurisdiction (AHJ)</h3>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="ahj" className="text-sm font-medium text-foreground/80">
                          Select AHJ
                        </Label>
                        <Select onValueChange={(value) => {
                          if (value === "add-new") {
                            setAhjDialogOpen(true);
                          } else {
                            setValue("ahj", value === "none" ? undefined : value);
                          }
                        }}>
                          <SelectTrigger id="ahj" className="bg-background/50 focus:bg-background transition-colors">
                            <SelectValue placeholder="Select AHJ" />
                          </SelectTrigger>
                          <SelectContent>
                            <div className="sticky top-0 bg-background border-b z-10">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAhjDialogOpen(true);
                                }}
                                className="w-full px-2 py-2 text-sm font-medium text-primary hover:bg-accent flex items-center gap-2 transition-colors"
                              >
                                <Plus className="h-4 w-4" />
                                Add New AHJ
                              </button>
                            </div>
                            <SelectItem value="none">None</SelectItem>
                            {Array.from(
                              (ahjsData?.ahjs || [])
                                .sort((a, b) => {
                                  if (a.type !== b.type) return a.type.localeCompare(b.type);
                                  if (a.state !== b.state) return a.state.localeCompare(b.state);
                                  return a.name.localeCompare(b.name);
                                })
                                .reduce((acc, ahj) => {
                                  const groupKey = `${ahj.type}-${ahj.state}`;
                                  if (!acc.has(groupKey)) {
                                    acc.set(groupKey, []);
                                  }
                                  acc.get(groupKey)?.push(ahj);
                                  return acc;
                                }, new Map<string, IAHJ[]>())
                                .entries()
                            ).map(([groupKey, ahjs]) => {
                              const [type, state] = groupKey.split('-');
                              return (
                                <div key={groupKey}>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                                    {type.charAt(0).toUpperCase() + type.slice(1)} - {state}
                                  </div>
                                  {ahjs.map((ahj: IAHJ) => (
                                    <SelectItem key={ahj._id} value={ahj._id}>
                                      {ahj.name} ({ahj.state})
                                    </SelectItem>
                                  ))}
                                </div>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-border/50">
                    <h3 className="font-semibold text-lg">Project Location</h3>
                    <div className="grid gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="address" className="text-sm font-medium text-foreground/80">
                          Address <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Input id="address" className="bg-background/50 focus:bg-background transition-colors" {...register("address")} />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-sm font-medium text-foreground/80">
                            City <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Input id="city" className="bg-background/50 focus:bg-background transition-colors" {...register("city")} />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="state" className="text-sm font-medium text-foreground/80">
                            State <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Input id="state" className="bg-background/50 focus:bg-background transition-colors" {...register("state")} />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="zip" className="text-sm font-medium text-foreground/80">
                            ZIP Code <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Input id="zip" className="bg-background/50 focus:bg-background transition-colors" {...register("zip")} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t border-border/50">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/projects")}
                    className="hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gradient flex items-center shadow-md hover:shadow-lg transition-all"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    {isSubmitting ? "Creating..." : "Begin Submission"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      <AHJFormDialog
        open={ahjDialogOpen}
        onOpenChange={setAhjDialogOpen}
        onSuccess={handleAHJCreated}
      />
    </>
  );
};

export default NewProject;
